import React, { useState, useRef, useEffect } from 'react';

const LayersPanel = ({
    layers,
    selectedLayer,
    isLayersPanelCollapsed,
    setIsLayersPanelCollapsed,
    isEditMode,
    handleLayerClick,
    toggleLayerVisibility,
    updateLayerColor,
    deleteLayer,
    moveLayer,
    onEditText,
    onRename
}) => (
    <div style={{
        position: 'absolute',
        right: '16px',
        top: '16px',
        background: 'white',
        padding: '12px',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
        width: isLayersPanelCollapsed ? 'auto' : 'min(300px, 25vw)',
        maxWidth: '90vw',
        transition: 'all 0.3s ease',
        border: '1px solid rgba(0, 0, 0, 0.1)'
    }}>
        <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer',
            userSelect: 'none',
            padding: '2px 0'
        }}
        onClick={() => setIsLayersPanelCollapsed(!isLayersPanelCollapsed)}>
            <div style={{ 
                fontWeight: '600',
                fontSize: '14px',
                color: '#333'
            }}>
                Layers
            </div>
            <button style={{
                background: 'none',
                border: 'none',
                padding: '8px',
                cursor: 'pointer',
                color: '#666',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                ':hover': {
                    background: '#f5f5f5'
                }
            }}>
                {isLayersPanelCollapsed ? '▼' : '▲'}
            </button>
        </div>
        
        {!isLayersPanelCollapsed && (
            <div style={{
                marginTop: '8px',
                maxHeight: '50vh',
                overflowY: 'auto',
                overflowX: 'hidden'
            }}>
                {[...layers].reverse().map((layer, index) => (
                    <LayerRow
                        key={layer.id}
                        layer={layer}
                        index={layers.length - 1 - index}
                        isSelected={selectedLayer === layer.id}
                        totalLayers={layers.length}
                        onLayerClick={handleLayerClick}
                        onVisibilityToggle={toggleLayerVisibility}
                        onColorUpdate={updateLayerColor}
                        onDelete={deleteLayer}
                        onMove={moveLayer}
                        onEditText={onEditText}
                        onRename={onRename}
                    />
                ))}
            </div>
        )}
    </div>
);

const LayerRow = ({
    layer,
    index,
    isSelected,
    totalLayers,
    onLayerClick,
    onVisibilityToggle,
    onColorUpdate,
    onDelete,
    onMove,
    onEditText,
    onRename
}) => {
    const [isRenaming, setIsRenaming] = useState(false);
    const [newName, setNewName] = useState(layer.name || 'Untitled');
    const inputRef = useRef(null);

    useEffect(() => {
        if (isRenaming && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isRenaming]);

    const handleRenameSubmit = () => {
        onRename(layer.id, newName);
        setIsRenaming(false);
    };

    return (
        <div 
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 8px',
                background: isSelected ? 'rgba(74, 134, 232, 0.1)' : 'transparent',
                borderRadius: '6px',
                marginBottom: '2px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                ':hover': {
                    background: isSelected ? 'rgba(74, 134, 232, 0.15)' : 'rgba(0, 0, 0, 0.05)'
                }
            }}
            onClick={(e) => {
                if (e.target.type === 'color') return;
                onLayerClick(layer.id);
            }}
        >
            <input
                type="checkbox"
                checked={layer.visible}
                onChange={(e) => {
                    e.stopPropagation();
                    onVisibilityToggle(layer.id);
                }}
                style={{ 
                    flexShrink: 0,
                    marginRight: '4px'
                }}
            />
            
            <div style={{ display: 'flex', gap: '2px', marginRight: '8px' }}>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onMove(index, 'down');
                    }}
                    disabled={index === totalLayers - 1}
                    style={{
                        padding: '2px 6px',
                        background: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        cursor: index === totalLayers - 1 ? 'default' : 'pointer',
                        opacity: index === totalLayers - 1 ? 0.5 : 1
                    }}
                >
                    ↑
                </button>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onMove(index, 'up');
                    }}
                    disabled={index === 0}
                    style={{
                        padding: '2px 6px',
                        background: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        cursor: index === 0 ? 'default' : 'pointer',
                        opacity: index === 0 ? 0.5 : 1
                    }}
                >
                    ↓
                </button>
            </div>

            {layer.type === 'shape' && (
                <input
                    type="color"
                    value={layer.data?.color || '#000000'}
                    onChange={(e) => {
                        e.stopPropagation();
                        console.log('Color change:', layer.id, e.target.value);
                        onColorUpdate(layer.id, e.target.value);
                    }}
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                    style={{
                        width: '24px',
                        height: '24px',
                        padding: 0,
                        border: 'none',
                        borderRadius: '4px',
                        marginRight: '8px',
                        cursor: 'pointer',
                        backgroundColor: 'transparent'
                    }}
                />
            )}
            {isRenaming ? (
                <input
                    ref={inputRef}
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onBlur={handleRenameSubmit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameSubmit();
                        if (e.key === 'Escape') {
                            setNewName(layer.name || 'Untitled');
                            setIsRenaming(false);
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        padding: '2px 4px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px'
                    }}
                />
            ) : (
                <span 
                    style={{ 
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        cursor: 'text'
                    }}
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                        setIsRenaming(true);
                    }}
                >
                    {layer.name || 'Untitled'}
                </span>
            )}
            
            {layer.data?.type === 'textbox' && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onEditText(layer.id);
                    }}
                    style={{
                        padding: '2px 6px',
                        background: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginLeft: '4px'
                    }}
                >
                    Edit
                </button>
            )}
            
            {layer.type !== 'image' && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(layer.id);
                    }}
                    style={{
                        padding: '2px 6px',
                        background: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        color: '#ff4444',
                        marginLeft: '4px'
                    }}
                >
                    ×
                </button>
            )}
        </div>
    );
};

export default LayersPanel; 